import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import client, { urlFor } from './sanityClient';
import Navbar from './Navbar';
import Footer from './Footer';

function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client
      .fetch(`*[_type == "post"] | order(publishedAt desc) {
        title, slug, excerpt, mainImage, publishedAt, primaryKeyword
      }`)
      .then((data) => { setPosts(data); setLoading(false); });
  }, []);

  if (loading) return (
    <section>
      <Navbar />
      <div style={styles.loading}>
        <i className="fa fa-paw" style={styles.loadingPaw}></i>
        <p>Loading posts...</p>
      </div>
      <Footer />
    </section>
  );

  return (
    <section>
      <Helmet>
        <title>Pet Health and Nutrition Blog - Furbabies.AI</title>
        <meta name="description" content="Expert tips on pet health, nutrition, and AI-powered care. Written for pet lovers who treat their furbabies like family." />
      </Helmet>

      <Navbar />

      <div style={styles.hero}>
        <div style={{position:'absolute',left:'8%',top:'30%',fontSize:'3rem',pointerEvents:'none'}}>
          <i className="fa fa-paw" style={{color:'white',opacity:0.4}}></i>
        </div>
        <div style={{position:'absolute',right:'10%',top:'50%',fontSize:'2rem',pointerEvents:'none'}}>
          <i className="fa fa-paw" style={{color:'white',opacity:0.3}}></i>
        </div>
        <motion.h1 style={styles.heroTitle} initial={{opacity:0,y:50}} animate={{opacity:1,y:0}} transition={{duration:1}}>
          Furbabies Blog
        </motion.h1>
        <motion.p style={styles.heroSubtitle} initial={{opacity:0}} animate={{opacity:0.7}} transition={{delay:0.2,duration:0.8}}>
          Pet health tips, nutrition guides, and pet care advice
        </motion.p>
      </div>

      <div style={styles.container}>
        {posts.length === 0 && (
          <div style={styles.empty}>
            <i className="fa fa-paw" style={{fontSize:'3rem',color:'#bda697',marginBottom:'16px'}}></i>
            <p>No posts yet - check back soon!</p>
          </div>
        )}
        <div style={styles.grid}>
          {posts.map((post, i) => (
            <motion.div key={post.slug.current} initial={{opacity:0,y:40}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6,delay:i*0.1}}>
              <Link to={`/blog/${post.slug.current}`} style={styles.card}
                onMouseEnter={e=>{e.currentTarget.style.transform='scale(1.02)';e.currentTarget.style.boxShadow='0 8px 32px rgba(0,0,0,0.12)'}}
                onMouseLeave={e=>{e.currentTarget.style.transform='scale(1)';e.currentTarget.style.boxShadow='0 4px 20px rgba(0,0,0,0.08)'}}
              >
                {post.mainImage ? (
                  <img src={urlFor(post.mainImage).width(600).height(340).url()} alt={post.title} style={styles.cardImage} loading="lazy" width="600" height="340" />
                ) : (
                  <div style={styles.cardImagePlaceholder}>
                    <i className="fa fa-paw" style={{fontSize:'3rem',color:'#bda697'}}></i>
                  </div>
                )}
                <div style={styles.cardBody}>
                  {post.primaryKeyword && <span style={styles.tag}>{post.primaryKeyword}</span>}
                  <h2 style={styles.cardTitle}>{post.title}</h2>
                  <p style={styles.cardExcerpt}>{post.excerpt}</p>
                  <div style={styles.cardFooter}>
                    <span style={styles.date}>
                      {new Date(post.publishedAt).toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})}
                    </span>
                    <span style={styles.readMore}>Read more</span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      <section style={styles.ctaSection}>
        <motion.h2 style={styles.ctaTitle} initial={{opacity:0}} whileInView={{opacity:1}} viewport={{once:true}} transition={{duration:0.5}}>
          Give Your Furbaby the Best Care
        </motion.h2>
        <p style={styles.ctaText}>AI-powered health assessments and personalized nutrition from the comfort of your home.</p>
        <Link to="/signup" style={styles.ctaButton}>Sign Up for Free</Link>
      </section>

      <Footer />
    </section>
  );
}

const styles = {
  loading:{textAlign:'center',padding:'120px 20px',fontFamily:"'Poppins',sans-serif",fontSize:'1.2rem',color:'#967d6c',display:'flex',flexDirection:'column',alignItems:'center'},
  loadingPaw:{fontSize:'3rem',color:'#bda697',marginBottom:'16px',display:'block'},
  hero:{background:'#BDA697',textAlign:'center',padding:'80px 20px 80px',marginTop:'11rem',position:'relative',overflow:'hidden'},
  heroTitle:{fontFamily:"'Poltawski Nowy',serif",fontSize:'clamp(3rem,8vw,5rem)',fontWeight:'bold',color:'white',marginBottom:'16px',lineHeight:'1.1'},
  heroSubtitle:{fontFamily:"'Poppins',sans-serif",fontSize:'1.1rem',color:'#222222',opacity:0.7},
  container:{maxWidth:'1100px',margin:'0 auto',padding:'60px 20px'},
  grid:{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:'32px'},
  card:{display:'block',textDecoration:'none',color:'inherit',borderRadius:'16px',overflow:'hidden',background:'#ffffff',boxShadow:'0 4px 20px rgba(0,0,0,0.08)',transition:'transform 0.2s ease,box-shadow 0.2s ease',height:'100%'},
  cardImage:{width:'100%',height:'220px',objectFit:'cover',display:'block'},
  cardImagePlaceholder:{width:'100%',height:'220px',background:'#e8e1da',display:'flex',alignItems:'center',justifyContent:'center'},
  cardBody:{padding:'24px'},
  tag:{display:'inline-block',background:'#e8e1da',color:'#967d6c',padding:'4px 12px',borderRadius:'20px',fontSize:'0.75rem',fontWeight:'600',fontFamily:"'Poppins',sans-serif",marginBottom:'12px',textTransform:'uppercase',letterSpacing:'0.5px'},
  cardTitle:{fontFamily:"'Poltawski Nowy',serif",fontSize:'1.3rem',fontWeight:'bold',color:'#222222',marginBottom:'10px',lineHeight:'1.4'},
  cardExcerpt:{fontFamily:"'Poppins',sans-serif",fontSize:'0.9rem',color:'#222222',opacity:0.6,lineHeight:'1.7',marginBottom:'20px'},
  cardFooter:{display:'flex',justifyContent:'space-between',alignItems:'center'},
  date:{fontFamily:"'Poppins',sans-serif",fontSize:'0.8rem',color:'#999'},
  readMore:{fontFamily:"'Poppins',sans-serif",fontSize:'0.9rem',color:'#967d6c',fontWeight:'700'},
  empty:{textAlign:'center',padding:'80px',fontFamily:"'Poppins',sans-serif",color:'#aaa',display:'flex',flexDirection:'column',alignItems:'center'},
  ctaSection:{background:'#e8e1da',textAlign:'center',padding:'80px 20px' , marginBottom:"4rem"},
  ctaTitle:{fontFamily:"'Poltawski Nowy',serif",fontSize:'clamp(2rem,5vw,3.5rem)',fontWeight:'bold',color:'#222222',marginBottom:'16px'},
  ctaText:{fontFamily:"'Poppins',sans-serif",fontSize:'1.1rem',color:'#222222',opacity:0.6,marginBottom:'32px'},
  ctaButton:{display:'inline-block',background:'#967d6c',color:'white',padding:'16px 48px',borderRadius:'9999px',fontFamily:"'Poppins',sans-serif",fontWeight:'bold',fontSize:'1.1rem',textDecoration:'none'},
};

export default Blog;